import React, { createContext, useContext, useEffect, useRef, useCallback, ReactNode } from 'react';
import { useAuth } from '../../hooks/useAuth';
import { TokenStorage } from '../../utils/token-storage';

export interface AppointmentEvent {
  type: 'connection' | 'heartbeat' | 'appointment_updated' | 'appointment_status_changed' | 'appointment_cancelled' | 'appointment_confirmed' | 'appointment_started' | 'appointment_completed' | 'appointment.join_status.updated';
  appointmentId?: string;
  userId?: string;
  data?: any;
  timestamp: string;
  message?: string;
}

interface AppointmentSSEContextType {
  subscribe: (callback: (event: AppointmentEvent) => void) => () => void;
  isConnected: boolean;
  connectionStatus: 'connecting' | 'connected' | 'disconnected' | 'error';
}

const AppointmentSSEContext = createContext<AppointmentSSEContextType | null>(null);

interface AppointmentSSEProviderProps {
  children: ReactNode;
}

export function AppointmentSSEProvider({ children }: AppointmentSSEProviderProps) {
  const { isAuthenticated, user } = useAuth();
  const eventSourceRef = useRef<EventSource | null>(null);
  const subscribersRef = useRef<Set<(event: AppointmentEvent) => void>>(new Set());
  const reconnectAttemptsRef = useRef(0);
  const maxReconnectAttempts = 3; // Reduced from 5 to 3
  const reconnectTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const [connectionStatus, setConnectionStatus] = React.useState<'connecting' | 'connected' | 'disconnected' | 'error'>('disconnected');

  // POLLING IMPLEMENTATION - SSE commented out
  const pollingIntervalRef = useRef<NodeJS.Timeout | null>(null);
  const POLLING_INTERVAL = 10000; // Poll every 10 seconds (reduced frequency)

  const startPolling = useCallback(() => {
    if (pollingIntervalRef.current) {
      clearInterval(pollingIntervalRef.current);
    }

    if (!isAuthenticated || !user?.id) {
      setConnectionStatus('disconnected');
      return;
    }

    setConnectionStatus('connected');
    console.log('Starting appointment polling...');

    // Poll for appointment updates
    pollingIntervalRef.current = setInterval(() => {
      // Send a generic update event to trigger data refetching
      // This matches the fallback behavior in the pages when no specific appointment ID is provided
      const pollingEvent: AppointmentEvent = {
        type: 'appointment_updated',
        timestamp: new Date().toISOString(),
        message: 'Polling update - refetching data'
      };

      // Notify all subscribers
      subscribersRef.current.forEach(callback => {
        try {
          callback(pollingEvent);
        } catch (error) {
          console.error('Error in polling subscriber callback:', error);
        }
      });
    }, POLLING_INTERVAL);
  }, [isAuthenticated, user?.id]);

  const stopPolling = useCallback(() => {
    if (pollingIntervalRef.current) {
      clearInterval(pollingIntervalRef.current);
      pollingIntervalRef.current = null;
    }
    setConnectionStatus('disconnected');
    console.log('Stopped appointment polling');
  }, []);

  // SSE IMPLEMENTATION - COMMENTED OUT
  /*
  const connect = useCallback(() => {
    if (eventSourceRef.current) {
      eventSourceRef.current.close();
    }

    if (!isAuthenticated || !user?.id) {
      setConnectionStatus('disconnected');
      return;
    }

    try {
      const accessToken = TokenStorage.getAccessToken();
      if (!accessToken) {
        console.warn('No access token available for SSE connection');
        setConnectionStatus('error');
        return;
      }

      // Check if token is expired
      if (TokenStorage.isAccessTokenExpired()) {
        console.warn('Access token is expired, skipping SSE connection');
        setConnectionStatus('error');
        return;
      }

      setConnectionStatus('connecting');

      // Create SSE connection with authentication
      const eventSource = new EventSource(
        `${process.env.REACT_APP_CORE_BACKEND_ENDPOINT || 'http://localhost:8210'}/api/sse/appointments?token=${encodeURIComponent(accessToken)}`
      );

      eventSource.onopen = () => {
        console.log('SSE connection opened');
        setConnectionStatus('connected');
        reconnectAttemptsRef.current = 0;
      };

      eventSource.onmessage = (event) => {
        try {
          const data: AppointmentEvent = JSON.parse(event.data);
          console.log('SSE event received:', data);
          
          // Notify all subscribers
          subscribersRef.current.forEach(callback => {
            try {
              callback(data);
            } catch (error) {
              console.error('Error in SSE subscriber callback:', error);
            }
          });
        } catch (error) {
          console.error('Failed to parse SSE event:', error);
        }
      };

      eventSource.onerror = (error) => {
        console.error('SSE connection error:', error);
        setConnectionStatus('error');
        
        // Attempt to reconnect with exponential backoff
        if (reconnectAttemptsRef.current < maxReconnectAttempts) {
          const delay = Math.min(1000 * Math.pow(2, reconnectAttemptsRef.current), 16000);
          reconnectAttemptsRef.current++;
          
          console.log(`Attempting to reconnect in ${delay}ms (attempt ${reconnectAttemptsRef.current}/${maxReconnectAttempts})`);
          
          reconnectTimeoutRef.current = setTimeout(() => {
            connect();
          }, delay);
        } else {
          console.error('Max reconnection attempts reached');
          setConnectionStatus('disconnected');
        }
      };

      eventSourceRef.current = eventSource;
    } catch (error) {
      console.error('Failed to create SSE connection:', error);
      setConnectionStatus('error');
    }
  }, [isAuthenticated, user?.id]);

  const disconnect = useCallback(() => {
    if (eventSourceRef.current) {
      eventSourceRef.current.close();
      eventSourceRef.current = null;
    }
    if (reconnectTimeoutRef.current) {
      clearTimeout(reconnectTimeoutRef.current);
      reconnectTimeoutRef.current = null;
    }
    setConnectionStatus('disconnected');
  }, []);
  */

  // Connect/disconnect based on authentication status - using polling instead of SSE
  useEffect(() => {
    if (isAuthenticated && user?.id) {
      startPolling();
    } else {
      stopPolling();
    }

    return () => {
      stopPolling();
    };
  }, [isAuthenticated, user?.id, startPolling, stopPolling]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      stopPolling();
    };
  }, [stopPolling]);

  const subscribe = useCallback((callback: (event: AppointmentEvent) => void) => {
    subscribersRef.current.add(callback);
    
    return () => {
      subscribersRef.current.delete(callback);
    };
  }, []);

  const contextValue: AppointmentSSEContextType = {
    subscribe,
    isConnected: connectionStatus === 'connected',
    connectionStatus,
  };

  return (
    <AppointmentSSEContext.Provider value={contextValue}>
      {children}
    </AppointmentSSEContext.Provider>
  );
}

export function useAppointmentSSE() {
  const context = useContext(AppointmentSSEContext);
  if (!context) {
    throw new Error('useAppointmentSSE must be used within an AppointmentSSEProvider');
  }
  return context;
}

export function useAppointmentSSESubscription(callback: (event: AppointmentEvent) => void) {
  const { subscribe } = useAppointmentSSE();
  
  useEffect(() => {
    return subscribe(callback);
  }, [subscribe, callback]);
}
